import React from "react";

const formatStatusResumo = (statusFrota) => {
  if (!statusFrota) {
    return "Sem status registrado";
  }

  const disponibilidade = ["vazio", "coleta", "negociacao", "descargaPendente"]
    .filter((flag) => statusFrota?.[flag])
    .map((flag) => flag.replace(/([A-Z])/g, " $1").trim())
    .join(" | ");

  const local = [statusFrota?.localCidade, statusFrota?.localEstado].filter(Boolean).join(" / ");
  const destino = statusFrota?.destino ? `Dest.: ${statusFrota.destino}` : "";

  return [disponibilidade || null, local || null, destino || null].filter(Boolean).join(" • ");
};

const RecommendationSection = ({
  loading,
  recommendationCards,
  pedidoRecomendado,
  selectedRecommendationIds,
  expandedRecommendations,
  onToggleDetails,
  onSelectRecommendation,
  extractOptionId,
  normalizeOptionValue,
}) => {
  if (loading) {
    return <div style={{ marginBottom: 16, color: "#666" }}>Carregando sugestoes de frota...</div>;
  }

  if (!Array.isArray(recommendationCards) || recommendationCards.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom: 20,
        padding: 16,
        borderRadius: 12,
        background: "#f7fafe",
        border: "1px solid #dbe8ff",
        boxShadow: "inset 0 0 6px rgba(43, 124, 255, 0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div style={{ fontWeight: 700, color: "#2b4c7e" }}>
          Sugestoes de frota{" "}
          {pedidoRecomendado?.NumeroDoPedido
            ? `para o pedido #${pedidoRecomendado.NumeroDoPedido}`
            : ""}
        </div>
        <div style={{ fontSize: 12, color: "#5170a2" }}>
          Clique em uma sugestao para adiciona-la abaixo
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {recommendationCards.map(({ dados, option, fallbackKey }, index) => {
          const extractedId = extractOptionId(option);
          const rawId =
            extractedId !== null
              ? extractedId
              : fallbackKey !== undefined && fallbackKey !== null
              ? fallbackKey
              : `__rec-${index}`;
          const recommendationKey = String(rawId);
          const optionNormalized = normalizeOptionValue(option);
          const selecionado = optionNormalized
            ? selectedRecommendationIds.has(optionNormalized)
            : selectedRecommendationIds.has(recommendationKey);
          const expandido = expandedRecommendations.has(recommendationKey);
          const resumoStatus = formatStatusResumo(dados?.statusFrota);

          const handleSummaryClick = () => onSelectRecommendation(option);

          return (
            <div
              key={recommendationKey}
              style={{
                flex: "1 1 320px",
                minWidth: 260,
                borderRadius: 12,
                border: `1px solid ${selecionado ? "#2b7cff" : "#dfe7fa"}`,
                background: selecionado ? "rgba(43, 124, 255, 0.08)" : "#ffffff",
                padding: 14,
                boxShadow: selecionado
                  ? "0 4px 16px rgba(43, 124, 255, 0.18)"
                  : "0 2px 8px rgba(15, 23, 42, 0.1)",
                transition: "all 0.2s ease",
                position: "relative",
              }}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={handleSummaryClick}
                onKeyPress={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleSummaryClick();
                  }
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 700, color: "#1f3c6d" }}>
                  Frota {dados?.frota ?? option?.label ?? "N/A"}
                </div>
                <div style={{ fontSize: 13, color: "#4b5c7a" }}>
                  Modelo: {dados?.modeloDescricao ?? "Sem modelo informado"}
                </div>
                <div style={{ fontSize: 12, color: "#65748b" }}>{resumoStatus}</div>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => onSelectRecommendation(option)}
                  style={{
                    background: "#2b7cff",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  {selecionado ? "Selecionado" : "Selecionar"}
                </button>
                <button
                  type="button"
                  onClick={() => onToggleDetails(recommendationKey)}
                  style={{
                    background: "transparent",
                    color: "#2b7cff",
                    border: "1px solid #2b7cff",
                    borderRadius: 8,
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  {expandido ? "Ocultar detalhes" : "Ver detalhes"}
                </button>
              </div>

              {Array.isArray(dados?.alertas) && dados.alertas.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 10px",
                    borderRadius: 8,
                    background: "#fff5f5",
                    border: "1px solid #fecaca",
                    color: "#9b1c1c",
                    fontSize: 12,
                  }}
                >
                  {dados.alertas.map((alerta, alertaIdx) => (
                    <div key={`${recommendationKey}-alerta-${alertaIdx}`}>• {alerta}</div>
                  ))}
                </div>
              )}

              {expandido && (
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 10,
                    borderTop: "1px dashed #d0d7e6",
                    fontSize: 12,
                    color: "#475569",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>Detalhes</div>
                  <div>Placa: {dados?.placa ?? "Não informado"}</div>
                  <div>Score: {dados?.score ?? "—"}</div>
                  <div>
                    Motivos:
                    <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                      {(dados?.motivos || []).map((motivo, motivoIdx) => (
                        <li key={`${recommendationKey}-motivo-${motivoIdx}`}>{motivo}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecommendationSection;
